'use client';
import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface Props {
  data: { month: string; retail: number; l1Bonus: number; eb: number; eeb: number; dblg1: number; breakaway: number }[];
  fullWidth?: boolean;
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s: number, p: any) => s + (p.value || 0), 0);
  return (
    <div className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 shadow-xl">
      <p className="text-xs font-semibold text-zinc-300 mb-1.5">{label}</p>
      {payload.map((p: any) => (
        <div key={`tt-${p.dataKey}`} className="flex items-center justify-between gap-4 text-xs">
          <span style={{ color: p.color }} className="font-medium">{p.name}</span>
          <span className="font-mono font-semibold text-zinc-200">${p.value?.toFixed(0)}</span>
        </div>
      ))}
      <div className="border-t border-zinc-700 mt-1.5 pt-1.5 flex justify-between text-xs">
        <span className="text-zinc-400">Total</span>
        <span className="font-mono font-bold text-emerald-400">${total.toFixed(0)}</span>
      </div>
    </div>
  );
};

export default function CommissionHistoryChart({ data, fullWidth }: Props) {
  return (
    <div className={`bg-zinc-900 border border-zinc-800 rounded-2xl p-5 ${fullWidth ? 'col-span-2' : ''}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-zinc-300">12-Month Commission History</h3>
        <span className="text-xs text-zinc-600">May 2025 – Apr 2026</span>
      </div>
      <ResponsiveContainer width="100%" height={fullWidth ? 280 : 220}>
        <BarChart data={data} margin={{ top: 4, right: 4, left: -15, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
          <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#71717a' }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 9, fill: '#71717a' }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v/1000).toFixed(0)}K`} />
          <Tooltip content={<CustomTooltip />} />
          <Legend iconSize={8} iconType="square" wrapperStyle={{ fontSize: '10px', paddingTop: '8px' }} />
          <Bar dataKey="retail" name="Retail" stackId="a" fill="#34d399" radius={[0,0,0,0]} />
          <Bar dataKey="l1Bonus" name="L1 Bonus" stackId="a" fill="#60a5fa" radius={[0,0,0,0]} />
          <Bar dataKey="eb" name="Exec Bonus" stackId="a" fill="#a78bfa" radius={[0,0,0,0]} />
          <Bar dataKey="eeb" name="EEB" stackId="a" fill="#c084fc" radius={[0,0,0,0]} />
          <Bar dataKey="dblg1" name="DBLG1" stackId="a" fill="#fbbf24" radius={[0,0,0,0]} />
          <Bar dataKey="breakaway" name="Breakaway" stackId="a" fill="#f87171" radius={[2,2,0,0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}